import ENDPOINTS from '@/config/apiEndpoints';
import APP_CONFIG from '@/config/app';
import {
  WalletInterface,
  WalletTokenInterface,
  WalletUnderlyingYieldBearingAssetInterface,
} from '@/interfaces';
import ApiService, { handleUnauthorized } from '@/services/ApiService';
import { formatToSentenceCase, sanitizeAssetAmount } from '@/utils/formatUtils';
import { generateLogoUri, generateTokenId } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
import { loadWalletsStub } from '@/utils/stubLoader';
const WALLET_REQUEST_CACHE = new Map<
  string,
  {
    timestamp: number;
    promise: Promise<WalletInterface[]>;
    inProgress: boolean;
  }
>();
const WALLET_THROTTLE_TIME_MS =
  APP_CONFIG.THROTTLE.WALLETS_THROTTLE_DURATION_IN_MS;
export const fetchWallets = async (): Promise<WalletInterface[]> => {
  const cacheKey = ENDPOINTS.USER_WALLETS;
  const now = Date.now();
  const cached = WALLET_REQUEST_CACHE.get(cacheKey);
  if (
    cached &&
    (cached.inProgress || now - cached.timestamp <= WALLET_THROTTLE_TIME_MS)
  ) {
    logInfo('Using cached wallet request', 'WalletsService.fetchWallets');
    return cached.promise;
  }
  const promise = (async () => {
    try {
      let response: any = null;
      const shouldUseStub =
        process.env.NODE_ENV === 'development' &&
        APP_CONFIG.STUBS?.USE_WALLETS_STUB;
      if (shouldUseStub) {
        response = await loadWalletsStub();
        if (response) {
          logInfo('Using stub data for wallets', 'WalletsService.fetchWallets');
        }
      }
      if (!response) {
        logInfo('Fetching wallets from API', 'WalletsService.fetchWallets');
        response = await ApiService.get(cacheKey);
      }
      if (response.isBetaRestricted) {
        logInfo(
          'Beta restriction encountered for wallets',
          'WalletsService.fetchWallets',
          response,
        );
        handleUnauthorized();
        return [];
      }
      logInfo(
        'Fetched wallets successfully',
        'WalletsService.fetchWallets',
        response,
      );
      if (!Array.isArray(response)) {
        logError(
          'Response is not an array',
          'WalletsService.fetchWallets',
          response,
        );
        return [];
      }
      const tokenColors = APP_CONFIG.PORTFOLIO_TOKEN_COLORS;
      const uniqueColors = new Set<string>();
      let colorIndex = 0;
      const assignUniqueColor = () => {
        let color = `${
          tokenColors[colorIndex % tokenColors.length]
        } bg-opacity-80`;
        while (uniqueColors.has(color)) {
          colorIndex = (colorIndex + 1) % tokenColors.length;
          color = `${tokenColors[colorIndex]} bg-opacity-80`;
        }
        uniqueColors.add(color);
        colorIndex++;
        return color;
      };
      const calculateBalanceInUsd = (asset: any) => {
        const balanceInUsd = parseFloat(asset.balanceInUsd);
        if (balanceInUsd > 0) {
          return balanceInUsd;
        }
        const balance = parseFloat(asset.balance);
        const priceInUsd = parseFloat(asset.priceInUsd);
        if (balance > 0 && priceInUsd > 0) {
          return balance * priceInUsd;
        }
        return 0;
      };
      response.forEach((wallet: any) => {
        wallet.balances.forEach((token: any) => {
          token.balanceInUsd = calculateBalanceInUsd(token);
        });
        wallet.nativeAssetBalances.forEach((asset: any) => {
          asset.balanceInUsd = calculateBalanceInUsd(asset);
        });
      });
      const totalPortfolioValueInUsd = response.reduce(
        (sum: number, wallet: any) =>
          sum +
          wallet.balances.reduce(
            (balanceSum: number, token: any) =>
              balanceSum + parseFloat(token.balanceInUsd || 0),
            0,
          ) +
          wallet.nativeAssetBalances.reduce(
            (assetSum: number, asset: any) =>
              assetSum + parseFloat(asset.balanceInUsd || 0),
            0,
          ),
        0,
      );
      const enrichedWallets: WalletInterface[] = response.map((wallet: any) => {
        const {
          address,
          blockchain,
          userId,
          nativeAssetBalances = [],
        } = wallet;
        const name = formatToSentenceCase(blockchain);
        const walletBlockchainDisplayName =
          APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
            blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
          ] || blockchain;
        const aggregatedBalanceInUsd =
          wallet.balances.reduce(
            (sum: number, token: any) =>
              sum + parseFloat(token.balanceInUsd || 0),
            0,
          ) +
          nativeAssetBalances.reduce(
            (sum: number, asset: any) =>
              sum + parseFloat(asset.balanceInUsd || 0),
            0,
          );
        const enrichedBalances: WalletTokenInterface[] = wallet.balances.map(
          (token: any): WalletTokenInterface => ({
            ...generateTokenId({
              ...token,
              blockchainDisplayName:
                APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
                  token.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
                ] || token.blockchain,
              balance: sanitizeAssetAmount(token.balance),
              priceInUsd: sanitizeAssetAmount(token.priceInUsd),
              balanceInUsd: parseFloat(token.balanceInUsd),
              walletName: walletBlockchainDisplayName,
              walletAddress: address,
              walletBlockchain: blockchain,
              walletBlockchainDisplayName,
            }),
            logoUri: generateLogoUri(token.symbol, token.logoUri),
            color: assignUniqueColor(),
            shareOfPortfolio:
              totalPortfolioValueInUsd > 0
                ? parseFloat(token.balanceInUsd || 0) / totalPortfolioValueInUsd
                : 0,
          }),
        );
        const enrichedUnderlyingYieldBearingAssets: WalletUnderlyingYieldBearingAssetInterface[] =
          nativeAssetBalances.map(
            (asset: any): WalletUnderlyingYieldBearingAssetInterface => {
              const enrichedAsset: WalletUnderlyingYieldBearingAssetInterface =
                {
                  ...generateTokenId({
                    ...asset,
                    isUnderlyingYieldBearingAsset: true,
                    blockchainDisplayName:
                      APP_CONFIG.MAPPINGS
                        .BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
                        asset.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
                      ] || asset.blockchain,
                    balance: sanitizeAssetAmount(asset.balance),
                    rewards: sanitizeAssetAmount(asset.rewards),
                    priceInUsd: parseFloat(asset.priceInUsd),
                    balanceInUsd: sanitizeAssetAmount(asset.balanceInUsd),
                    rewardsInUsd: sanitizeAssetAmount(asset.rewardsInUsd),
                    walletName: walletBlockchainDisplayName,
                    walletAddress: address,
                    walletBlockchain: blockchain,
                    walletBlockchainDisplayName,
                  }),
                  logoUri: generateLogoUri(asset.symbol, asset.logoUri),
                  color: assignUniqueColor(),
                  shareOfPortfolio:
                    totalPortfolioValueInUsd > 0
                      ? parseFloat(asset.balanceInUsd || 0) /
                        totalPortfolioValueInUsd
                      : 0,
                };
              if (asset.underlyingAssets) {
                enrichedAsset.underlyingAssets = asset.underlyingAssets.map(
                  (underlyingAsset: any): WalletTokenInterface => ({
                    ...generateTokenId({
                      ...underlyingAsset,
                      blockchainDisplayName:
                        APP_CONFIG.MAPPINGS
                          .BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME[
                          underlyingAsset.blockchain as keyof typeof APP_CONFIG.MAPPINGS.BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME
                        ] || underlyingAsset.blockchain,
                      balance: sanitizeAssetAmount(underlyingAsset.balance),
                      priceInUsd: sanitizeAssetAmount(
                        underlyingAsset.priceInUsd,
                      ),
                      balanceInUsd: parseFloat(underlyingAsset.balanceInUsd),
                      walletName: walletBlockchainDisplayName,
                      walletAddress: address,
                      walletBlockchain: blockchain,
                      walletBlockchainDisplayName,
                    }),
                    logoUri: generateLogoUri(
                      underlyingAsset.symbol,
                      underlyingAsset.logoUri,
                    ),
                    color: assignUniqueColor(),
                    shareOfPortfolio:
                      totalPortfolioValueInUsd > 0
                        ? parseFloat(underlyingAsset.balanceInUsd || 0) /
                          totalPortfolioValueInUsd
                        : 0,
                  }),
                );
              }
              return enrichedAsset;
            },
          );
        return {
          name,
          address,
          blockchain,
          balances: enrichedBalances,
          underlyingYieldBearingAssetBalances:
            enrichedUnderlyingYieldBearingAssets,
          aggregatedBalanceInUsd,
          userId,
          blockchainDisplayName: walletBlockchainDisplayName,
          userPlanInfo: wallet.userPlanInfo,
        };
      });
      return enrichedWallets;
    } catch (error: any) {
      if (error.isBetaRestricted) {
        logInfo(
          'Beta restriction encountered for wallets',
          'WalletsService.fetchWallets',
          error,
        );
        handleUnauthorized();
        return [];
      }
      if (error.response) {
        const { status, data } = error.response;
        logError(
          `API error encountered - HTTP ${status}`,
          'WalletsService.fetchWallets',
          error,
        );
        if (
          status === 401 &&
          data?.errorCode === 'UNAUTHORIZED' &&
          data?.errorMessage === 'Not part of beta program'
        ) {
          logInfo(
            'Beta restriction encountered for wallets',
            'WalletsService.fetchWallets',
            error,
          );
          handleUnauthorized();
          return [];
        }
        throw error;
      }
      logError(error, 'WalletsService.fetchWallets');
      throw error;
    }
  })();
  WALLET_REQUEST_CACHE.set(cacheKey, {
    timestamp: now,
    promise,
    inProgress: true,
  });
  promise
    .then((result) => {
      const entry = WALLET_REQUEST_CACHE.get(cacheKey);
      if (entry && entry.timestamp === now) {
        WALLET_REQUEST_CACHE.set(cacheKey, {
          ...entry,
          inProgress: false,
        });
      }
      return result;
    })
    .catch((error) => {
      logError('Error in promise chain', 'WalletsService.fetchWallets', error);
      return [];
    });
  setTimeout(() => {
    const entry = WALLET_REQUEST_CACHE.get(cacheKey);
    if (entry && entry.timestamp === now && !entry.inProgress) {
      WALLET_REQUEST_CACHE.delete(cacheKey);
    }
  }, WALLET_THROTTLE_TIME_MS);
  return promise;
};
export const withdrawFromWallet = async (
  sourceWallet: WalletInterface,
  token: WalletTokenInterface,
  amountInTokenCurrency: number,
  destinationWalletAddress: string,
): Promise<any> => {
  logInfo('Initiating withdrawal', 'WalletsService.withdrawFromWallet', {
    walletAddress: sourceWallet.address,
    blockchain: sourceWallet.blockchain,
    amountInTokenCurrency,
    destinationWalletAddress,
  });
  const requestBody = {
    destinationAddress: destinationWalletAddress,
    token: {
      blockchain: token.blockchain,
      address: token.address,
    },
    amount: amountInTokenCurrency.toString(),
  };
  try {
    const response = await ApiService.post(
      ENDPOINTS.WITHDRAW_FROM_WALLET,
      requestBody,
    );
    logInfo(
      'Withdrawal successful',
      'WalletsService.withdrawFromWallet',
      response,
    );
    return response;
  } catch (error) {
    logError(error, 'WalletsService.withdrawFromWallet');
    throw new Error('Withdrawal failed');
  }
};
export const fetchWithdrawalableTokens = async (): Promise<
  (WalletTokenInterface | WalletUnderlyingYieldBearingAssetInterface)[]
> => {
  const wallets = await fetchWallets();
  const allTokens = wallets.flatMap((wallet) => [
    ...wallet.balances,
    ...wallet.underlyingYieldBearingAssetBalances,
  ]);
  return allTokens;
};
